/**
 * GachaPoolInfoModal Component
 *
 * Displays gacha pool contents for the selected generation/region
 * Shows which Pokemon the player has collected
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info } from 'lucide-react';
import {
  generatePokemonSprite,
  getRarityColor,
  applyLimitBreakToStats as applyLimitBreakToStatsUtil,
  getPokemonRarity as getPokemonRarityUtil
} from '../utils/gameUtils';
import { useGameData } from '../contexts/GameDataContext';
import { useInventory } from '../contexts/InventoryContext';
import { TypeBadge } from './TypeIcon';
import LimitBreakDiamonds from './LimitBreakDiamonds';
import PokemonDetailModal from './PokemonDetailModal';

const GachaPoolInfoModal = ({ isOpen, onClose, selectedGeneration }) => {
  const { pokemonInventory, getPokemonLimitBreak } = useInventory();
  const { POKEMON, GACHA_POOLS_BY_GENERATION, GENERATIONS, GACHA_RARITY, EVOLUTION_CHAINS } = useGameData();

  // Long-press state for detail modal
  const [detailPokemon, setDetailPokemon] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressPokemonRef = useRef(null);
  const LONG_PRESS_DURATION = 500; // 500ms hold to trigger

  const handleLongPressStart = useCallback((pokemonName) => {
    longPressPokemonRef.current = pokemonName;
    longPressTimerRef.current = setTimeout(() => {
      setDetailPokemon(longPressPokemonRef.current);
    }, LONG_PRESS_DURATION);
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressPokemonRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // Helper functions using gameUtils
  const getPokemonRarity = useCallback((pokemonName) => {
    return getPokemonRarityUtil(pokemonName, GACHA_RARITY, EVOLUTION_CHAINS);
  }, [GACHA_RARITY, EVOLUTION_CHAINS]);

  const applyLimitBreakToStats = useCallback((baseStats, limitBreakLevel) => {
    return applyLimitBreakToStatsUtil(baseStats, limitBreakLevel);
  }, []);

  if (!isOpen || !selectedGeneration) return null;

  // Defensive checks - data loaded from GameDataContext
  if (!GACHA_POOLS_BY_GENERATION || !GENERATIONS || !pokemonInventory) {
    return null;
  }

  const generationData = GACHA_POOLS_BY_GENERATION[selectedGeneration];
  const generationName = GENERATIONS[selectedGeneration]?.name || 'Unknown';

  if (!generationData) {
    return null;
  }

  // Get player's collection for this generation
  const getCollectionStatus = () => {
    const status = {
      Common: { owned: [], total: 0 },
      Uncommon: { owned: [], total: 0 },
      Rare: { owned: [], total: 0 },
      Legendary: { owned: [], total: 0 }
    };

    Object.entries(generationData).forEach(([rarity, data]) => {
      if (!data.pokemon) return;

      status[rarity].total = data.pokemon.length;
      status[rarity].owned = data.pokemon.filter(pokemonName => pokemonInventory.includes(pokemonName));
    });

    return status;
  };

  const collectionStatus = getCollectionStatus();

  // Calculate total collection percentage
  const totalOwned = Object.values(collectionStatus).reduce((sum, { owned }) => sum + owned.length, 0);
  const totalPokemon = Object.values(collectionStatus).reduce((sum, { total }) => sum + total, 0);
  const collectionPercentage = totalPokemon > 0 ? Math.round((totalOwned / totalPokemon) * 100) : 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-card-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-pocket-text flex items-center gap-2">
                    <Info size={20} className="text-pocket-blue" />
                    {generationName} Pokemon Pool
                  </h2>
                  <p className="text-sm text-pocket-text-light mt-1">
                    Collection: {totalOwned}/{totalPokemon} ({collectionPercentage}%)
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-pocket-text-light hover:text-pocket-text hover:bg-pocket-bg rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Collection Progress Bar */}
              <div className="mt-3">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${collectionPercentage}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-pocket-blue to-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Pool Contents by Rarity */}
            <div className="p-4 space-y-4">
              {['Legendary', 'Rare', 'Uncommon', 'Common'].map(rarity => {
                const rarityData = generationData[rarity];
                if (!rarityData || !rarityData.pokemon || rarityData.pokemon.length === 0) return null;

                const { owned, total } = collectionStatus[rarity];
                const ownedCount = owned.length;
                const ownedPercentage = total > 0 ? Math.round((ownedCount / total) * 100) : 0;

                return (
                  <div key={rarity} className="bg-pocket-bg rounded-xl p-4">
                    {/* Rarity Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="px-3 py-1 rounded-full text-white text-sm font-bold"
                          style={{ backgroundColor: getRarityColor(rarity) }}
                        >
                          {rarity}
                        </span>
                        <span className="text-xs text-pocket-text-light">
                          {(rarityData.rate * 100).toFixed(1)}% drop rate
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-pocket-text">
                        {ownedCount}/{total} ({ownedPercentage}%)
                      </div>
                    </div>

                    {/* Pokemon Grid */}
                    <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2">
                      {rarityData.pokemon.map(pokemonName => {
                        const isOwned = pokemonInventory.includes(pokemonName);
                        const pokemonData = POKEMON[pokemonName];
                        const limitBreak = isOwned ? getPokemonLimitBreak(pokemonName) : 0;

                        return (
                          <motion.div
                            key={pokemonName}
                            whileHover={{ scale: 1.05 }}
                            onMouseDown={() => handleLongPressStart(pokemonName)}
                            onMouseUp={handleLongPressEnd}
                            onMouseLeave={handleLongPressEnd}
                            onTouchStart={() => handleLongPressStart(pokemonName)}
                            onTouchEnd={handleLongPressEnd}
                            className={`relative rounded-lg p-2 transition-all cursor-pointer ${
                              isOwned
                                ? 'bg-white border-2 border-green-400 shadow-sm'
                                : 'bg-gray-100 border-2 border-gray-300 opacity-40'
                            }`}
                            title={pokemonName}
                          >
                            {/* NEW badge for unowned */}
                            {!isOwned && (
                              <div className="absolute -top-1 -right-1 z-10">
                                <span className="bg-red-500 text-white text-[8px] font-bold px-1 rounded">
                                  NEW
                                </span>
                              </div>
                            )}

                            {/* Pokemon Sprite */}
                            <div className="aspect-square flex items-center justify-center">
                              <div className={`w-full h-full flex items-center justify-center ${!isOwned ? 'grayscale' : ''}`}>
                                {generatePokemonSprite(
                                  pokemonData?.primaryType || 'Normal',
                                  pokemonName,
                                  48,
                                  false
                                )}
                              </div>
                            </div>

                            {/* Limit Break Diamonds (only if owned) */}
                            {isOwned && (
                              <div className="flex justify-center -mt-1">
                                <LimitBreakDiamonds level={limitBreak} size={4} />
                              </div>
                            )}

                            {/* Pokemon Name (on hover) */}
                            <div className="absolute inset-0 bg-black bg-opacity-80 rounded-lg opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center p-1">
                              <span className="text-white text-[8px] font-bold text-center leading-tight">
                                {pokemonName}
                              </span>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 rounded-b-2xl">
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-pocket-blue text-white font-semibold hover:bg-blue-600 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Pokemon Detail Modal (Long Press) */}
      {detailPokemon && POKEMON[detailPokemon] && (
        <PokemonDetailModal
          pokemonName={detailPokemon}
          onClose={() => setDetailPokemon(null)}
          getPokemonRarity={getPokemonRarity}
          getPokemonLimitBreak={getPokemonLimitBreak}
          applyLimitBreakToStats={applyLimitBreakToStats}
        />
      )}
    </AnimatePresence>
  );
};

export default GachaPoolInfoModal;
